import { remoteManualList, readManualPage, acceptManualPage } from '../../services/manual-list.js';
import { listSummary } from '../../presentation/list-copy';
// The shared device/scene list owns all page reads, including action-bound details. Child cards only read again for an explicit
// manual open/close; no per-card initialization requests or whole-list refresh.
import { createAiClient, identity, errorMessage } from '@/ai/services/api';
export default {
  data: () => ({ devicePage: null, deviceLoading: false, deviceError: false, deviceErrorText: '' }),
  computed: {
    remoteDeviceList() { return remoteManualList(this.card); },
    displayCard() {
      if (!this.remoteDeviceList) return this.card;
      const page = this.devicePage;
      return { ...this.card, ...(page || {}), items: page ? page.items : [],
        hasMore: !!page && page.hasMore, nextCursor: page ? page.nextCursor : null,
        footerText: page ? listSummary(page.items, page.matchedTotal) : this.card.footerText };
    },
    devicePagination() { return this.remoteDeviceList
      ? { loading: this.deviceLoading, error: this.deviceError, errorMessage: this.deviceErrorText } : this.pagination; }
  },
  watch: {
    blocked(blocked) { if (blocked) this.closeDeviceList(); },
    manualDisabled(disabled) { if (!disabled && !this._deviceStarted && (this._deviceVisible || !this._deviceObserver)) this.startDeviceList(); },
    devicePage() { this.$nextTick(() => { if (this.resizeDeviceGroup) this.resizeDeviceGroup(); }); },
    card(value, previous) {
      if (JSON.stringify(value.source) !== JSON.stringify(previous && previous.source)) this.restartDeviceList();
    },
    'aiPageState.valid'(valid) {
      if (!valid) this.closeDeviceList();
      else if (!this._deviceStarted && (this._deviceVisible || !this._deviceObserver)) this.startDeviceList();
    }
  },
  mounted() {
    this._deviceAlive = true; this._deviceGeneration = 0;
    // Only visible chat lists read. Offscreen historical lists do not fan out requests.
    if (typeof IntersectionObserver === 'undefined') this.startDeviceList();
    else {
      this._deviceObserver = new IntersectionObserver(entries => {
        if (entries.some(entry => entry.isIntersecting)) { this._deviceVisible = true; this.startDeviceList(); }
      });
      this._deviceObserver.observe(this.$el);
    }
  },
  beforeDestroy() { this._deviceAlive = false; this.closeDeviceList(); if (this._deviceObserver) this._deviceObserver.disconnect(); },
  methods: {
    closeDeviceList() {
      this._deviceGeneration = (this._deviceGeneration || 0) + 1;
      if (this._deviceClient) this._deviceClient.close();
      this._deviceClient = null; this._deviceStarted = false; this.deviceLoading = false;
    },
    restartDeviceList() { this.closeDeviceList(); this.devicePage = null; this.deviceError = false; this.deviceErrorText = ''; if (this._deviceVisible || !this._deviceObserver) this.startDeviceList(); },
    startDeviceList() {
      if (!this._deviceAlive || !this.remoteDeviceList || this.manualDisabled || this._deviceStarted ||
          (this.aiPageState && this.aiPageState.valid === false)) return;
      this._deviceStarted = true; this.readDevicePage(true);
    },
    async readDevicePage(first = false, retry = false) {
      if (!this._deviceAlive || !this.remoteDeviceList || this.manualDisabled || this.deviceLoading ||
          (this.aiPageState && this.aiPageState.valid === false) ||
          (!first && (!this.displayCard.hasMore || (this.deviceError && !retry)))) return;
      const source = this.card.source, scope = identity(), generation = this._deviceGeneration;
      const current = () => this._deviceAlive && generation === this._deviceGeneration && !this.blocked &&
        JSON.stringify(source) === JSON.stringify(this.card.source) && scope === identity() && (!this.aiPageState || this.aiPageState.valid !== false);
      const client = this._deviceClient = createAiClient();
      this.deviceLoading = true; this.deviceError = false; this.deviceErrorText = '';
      try {
        const cursor = first ? null : this.displayCard.nextCursor;
        const page = await readManualPage(client, source, cursor);
        if (!current()) return;
        this.devicePage = acceptManualPage(this.card, page, cursor, first ? [] : this.devicePage.items);
      } catch (error) { if (current()) { this.deviceError = true; this.deviceErrorText = errorMessage(error, ''); } }
      finally {
        client.close();
        if (this._deviceClient === client) this._deviceClient = null;
        if (this._deviceAlive && generation === this._deviceGeneration) this.deviceLoading = false;
      }
    },
    requestDevicePage(retry = false) {
      if (this.remoteDeviceList) return this.readDevicePage(!this.devicePage, retry);
      if (this.manualDisabled || this.pagination.loading || !this.card.hasMore || (!retry && (this.pagination.error || this.devicePageAttempt === this.devicePageKey()))) return;
      this.devicePageAttempt = this.devicePageKey(); this.$emit('page', this.card);
    },
    loadDevicesOnScroll() {
      const box = this.$refs.deviceScroll;
      if (box && box.scrollHeight > box.clientHeight &&
          box.scrollHeight - box.scrollTop - box.clientHeight < 16) this.requestDevicePage();
    }
  }
};
